import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type BookmarkQuestion } from '../lib/api';

export function Bookmarks() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<BookmarkQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getBookmarks()
      .then((r) => !cancelled && setQuestions(r.questions))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  async function remove(id: string) {
    setBusy(id);
    try {
      await api.toggleBookmark(id);
      setQuestions((qs) => qs.filter((q) => q.id !== id));
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ paddingTop: 20 }}>
      <button className="link" style={{ background: 'none', border: 'none', marginBottom: 8 }} onClick={() => navigate(-1)}>‹ Back</button>
      <h1 style={{ fontSize: 24, fontWeight: 900 }}>🔖 Bookmarked Questions</h1>
      <p className="muted" style={{ marginBottom: 18 }}>Your saved questions for quick revision</p>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : questions.length === 0 ? (
        <div className="col center" style={{ padding: 50, gap: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 48 }}>🔖</div>
          <div style={{ fontWeight: 800 }}>No bookmarks yet</div>
          <p className="muted" style={{ maxWidth: 340 }}>Tap the bookmark icon on any question in a quiz or test-series review to save it here.</p>
          <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => navigate('/store')}>Browse quizzes</button>
        </div>
      ) : (
        questions.map((q, i) => (
          <div key={q.id} className="card" style={{ marginBottom: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ fontWeight: 700, lineHeight: 1.4 }}>
                <span className="badge" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', marginRight: 8 }}>
                  {q.year ? `${q.year}${q.session ? ` · ${q.session}` : ''}` : q.type.toUpperCase()}
                </span>
                {i + 1}. {q.text}
              </div>
              <button
                className="icon-btn"
                style={{ background: 'var(--primary-soft)' }}
                onClick={() => remove(q.id)}
                disabled={busy === q.id}
                title="Remove bookmark"
              >
                🔖
              </button>
            </div>
            <div className="col gap8" style={{ marginTop: 10 }}>
              {q.options.map((opt, oi) => {
                const correct = oi === q.answer;
                return (
                  <div key={oi} className="row" style={{ justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: correct ? 'var(--success-soft)' : 'var(--surface-alt)' }}>
                    <span style={{ fontWeight: correct ? 700 : 400, color: correct ? 'var(--text)' : 'var(--muted)' }}>{String.fromCharCode(65 + oi)}. {opt}</span>
                    {correct && <span style={{ color: 'var(--success)' }}>✓</span>}
                  </div>
                );
              })}
            </div>
            {q.solution && <p className="muted" style={{ marginTop: 10, lineHeight: 1.5 }}><b>Solution: </b>{q.solution}</p>}
          </div>
        ))
      )}
    </div>
  );
}
